<template>
  <div id="asset-list">
    <b-container>
      <h1>{{ $t('ASSET_LIST') }} ({{ total }})</h1>

      <div class="filters">
        <filter-box class="feature-button" />
        <date-filter
          class="feature-button"
          pure-icon
          white
          :from="filters.fromDate"
          :to="filters.toDate"
          :presets="datePresets"
          @filter="setDate"
        />
        <text-input
          :placeholder="$t('SEACH_NAME_OR_ID')"
          v-model="searchInput"
          @keyup.enter="search"
        >
          <template v-slot:icon>
            <font-awesome-icon class="icon" :icon="faSearch" />
          </template>
        </text-input>
      </div>
      <b-row class="list">
        <b-col
          xs="12" sm="6" lg="4"
          v-for="asset in items"
          :key="asset.id"
        >
          <div class="item">
            <div>
              <a @click="showEditAsset(asset.id)">
                <h3>
                  <span>#{{ asset.code }}</span>
                </h3>
              </a>
            </div>
            <div class="info">
              <table>
                <tr>
                  <td>{{ $t('NAME_CLI') }}:</td>
                  <td>
                    <router-link v-if="asset.client" :to="applyParams(PATHS.CLIENT_DETAIL, asset.client.finfluxId)">
                      <h3>
                        <span>{{ asset.client.nameVi }}</span>
                      </h3>
                    </router-link>
                  </td>
                </tr>
                <tr>
                  <td>{{ $t('STORAGE') }}:</td>
                  <td>{{ getStorageName(asset.storageId) }} </td>
                </tr>
                <tr>
                  <td>{{ $t('STATUS') }}:</td>
                  <td>{{ $t( asset.status ) }}</td>
                </tr>
                <tr>
                  <td>{{ $t('ASSET_TYPE') }}:</td>
                  <td>{{ $t(getCategoryName(asset.categoryId)) }} </td>
                </tr>
                <tr>
                  <td>{{ $t('CONTRACT_ID') }}:</td>
                  <td class="d-flex flex-wrap">
                    <router-link
                      v-for="(contract, index) in asset.contracts"
                      :key="index"
                      :to="applyParams(PATHS.CONTRACT_DETAIL, contract)"
                    >
                      <h3>
                        <span>{{ contract }}<span v-if="(index !== asset.contracts.length - 1)" class="space-comma">,</span></span>
                      </h3>
                    </router-link>
                  </td>
                </tr>
                <tr>
                  <td>{{ $t('CREATED_DATE') }}:</td>
                  <td>{{ moment(asset.createdAt, DATE_FORMAT).format('DD/MM/YYYY') }}</td>
                </tr>
                <tr>
                  <td>{{ $t('LAST_MODIFIED') }}:</td>
                  <td>{{ moment(asset.modifiedAt, DATE_FORMAT).format('DD/MM/YYYY') }}</td>
                </tr>
                <tr v-if="asset.status === 'LIQUIDIZED' && asset.liquidizedOn">
                  <td>{{ $t('LIQUIDIZED') }}:</td>
                  <td>{{ moment(asset.liquidizedOn, DATE_FORMAT).format('DD/MM/YYYY') }} </td>
                </tr>
                <tr v-if="asset.status === 'RELEASED' && asset.releasedOn">
                  <td>{{ $t('RELEASED') }}:</td>
                  <td>{{ moment(asset.releasedOn, DATE_FORMAT).format('DD/MM/YYYY') }} </td>
                </tr>
              </table>
            </div>
          </div>
        </b-col>
      </b-row>

      <pagination
        :page="filters.page"
        :pageSize="filters.pageSize"
        :total="total"
        @pageSizeChange="changePageSize"
        @pageChange="changePage"
      />
      <edit-asset-info />
    </b-container>
  </div>
</template>

<script>
import { onMounted, ref, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSearch, faPhoneAlt } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

import { PATHS, DATE_FORMAT } from '@/consts';
import { applyParams } from '@/helpers';

import TextInput from '@/components/TextInput';
import Pagination from '@/components/Pagination';
import DateFilter from '@/components/DateFilter';
import FilterBox from './FilterBox';
import EditAssetInfo from './EditAssetInfo';

const { useState, useActions } = createNamespacedHelpers('assetsList');
const { useActions: useModalActions } = createNamespacedHelpers('modal');
const { useState: useAppState } = createNamespacedHelpers('app');

const now = moment();

const datePresets = [
  {
    text: 'TODAY',
    range: [now.format(DATE_FORMAT), now.format(DATE_FORMAT)]
  },
  {
    text: 'THIS_MONTH',
    range: [now.clone().startOf('month').format(DATE_FORMAT), now.format(DATE_FORMAT)]
  },
  {
    text: 'LAST_MONTH',
    range: [now.clone().subtract(1, 'month').startOf('month').format(DATE_FORMAT), now.clone().subtract(1, 'month').endOf('month').format(DATE_FORMAT)]
  },
  {
    text: 'THIS_YEAR',
    range: [now.clone().startOf('year').format(DATE_FORMAT), now.format(DATE_FORMAT)]
  }
];

export default {
  name: 'AssetsManagement',
  components: {
    Pagination,
    TextInput,
    FontAwesomeIcon,
    FilterBox,
    DateFilter,
    EditAssetInfo,
  },
  metaInfo() {
    const { $i18n } = this;
    const title = $i18n.t('ASSET_LIST');

    return {
      title,
      meta: []
    };
  },
  setup() {
    const { isDesktop, storages, assetCategories } = useAppState(['isDesktop', 'branches', 'storages', 'assetCategories']);
    const { filters, items, total } = useState(['filters', 'items', 'total']);
    const { applyFilter, getAssetsList, getAssetDetails } = useActions(['applyFilter', 'getAssetsList', 'getAssetDetails']);
    const { toggleEditAssetModal: toggleEditAsset } = useModalActions(['toggleEditAssetModal']);

    const searchInput = ref(filters.value.term);

    const getStorageName = (id) => {
      const storageName = storages.value.find((s) => s.id === id);
      return storageName?.name || '';
    };

    const getCategoryName = (id) => {
      const categoryName = assetCategories.value.find((c) => c.id === id);
      return categoryName?.key || '';
    };

    const search = () => {
      applyFilter({
        term: searchInput.value,
      });
    };

    const changePageSize = (value) => {
      applyFilter({
        pageSize: value,
        page: 1,
      });
    };

    const changePage = (value) => {
      applyFilter({
        page: value,
      });
    };

    const setDate = (from, to) => {
      applyFilter({
        fromDate: from,
        toDate: to,
        page: 1
      });
    };

    const showEditAsset = async (id) => {
      await getAssetDetails(id);
      toggleEditAsset();
    };

    onMounted(() => {
      getAssetsList();
    });

    watch(filters, () => {
      getAssetsList();
    });

    return {
      searchInput,
      search,
      changePageSize,
      changePage,
      filters,
      items,
      total,
      faSearch,
      faPhoneAlt,
      isDesktop,
      applyParams,
      PATHS,
      FilterBox,
      datePresets,
      setDate,
      EditAssetInfo,
      showEditAsset,
      getAssetDetails,
      getStorageName,
      getCategoryName,
      DATE_FORMAT,
      moment
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#asset-list {
  padding: 30px;

  .space-comma {
    padding-right: 3px;
  }

  .container {
    margin-left: 0;
  }

  h1 {
    font-size: 28px;
    text-transform: capitalize;
  }

  .filters {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 30px;

    .feature-button {
      .toggle-button {
        border-radius: 4px;
      }
    }

    .text-input {
      margin-left: 5px;
      width: 320px;

      input {
        border-radius: 4px;
        border-color: $alto;
      }

      .icon {
        top:20px;
      }
    }
  }

  .list {
    .item {
      background-color: $babyPowder;
      border-radius: 4px;
      padding: 15px;
      margin-left: -7.5px;
      margin-right: -7.5px;
      margin-bottom: 15px;
      position: relative;
      box-sizing: border-box;
      height: calc(100% - 15px);

      a {
        color: inherit;
        text-decoration: none;
      }

      h3 {
        font-size: 16px;
        font-weight: bold;
        text-transform: uppercase;
        display: flex;
        justify-content: space-between;
        color: $blueNcs;
        transition: all .2s ease-in-out;
        margin-bottom: 0;

        &:hover {
          color: $paradisePink;
        }
      }

      .info {
        table {
          tr {
            td {
              &:first-child {
                padding-right: 15px;
              }
            }
          }
        }
      }

      .call {
        position: absolute;
        bottom: 10px;
        right: 10px;
        border: 1px solid $alto;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        text-align: center;
        padding-top: 7px;
        cursor: pointer;

        color: $shamrockGreen;
        transition: all .2s ease-in-out;

        &:hover {
          color: $babyPowder;
          background-color: $shamrockGreen;
        }
      }
    }
  }
}
</style>
